import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSiteSettings, pick } from '@/contexts/SiteSettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, ArrowRight, ChevronRight, Shield, Clock, Users, TrendingUp, CheckCircle, Coins, Sparkles } from 'lucide-react';
import axios from 'axios';
import useReveal from '@/hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://images.pexels.com/photos/28406821/pexels-photo-28406821.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// ─── Animated Counter ───
function AnimatedCounter({ end, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        const numEnd = parseInt(String(end).replace(/[^0-9]/g, ''));
        const duration = 2000;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * numEnd));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

// ─── Hero Section ───
function HeroSection() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const displayFont = lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const isAr = lang === 'ar';
  const [liveRate, setLiveRate] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/rates`)
      .then(({ data }) => {
        if (!mounted) return;
        const rows = data?.resident || [];
        // Prefer 1-Year Fixed
        const best = rows.find(r => /1.?year fixed/i.test(r.product) && /fixed/i.test(r.rate_type))
                 || rows.find(r => /fixed/i.test(r.rate_type))
                 || rows[0];
        if (best) setLiveRate(best);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const heroTitle = pick(isAr ? settings?.content?.hero_title_ar : settings?.content?.hero_title_en, t('hero', 'h1'));
  const heroSub = pick(isAr ? settings?.content?.hero_sub_ar : settings?.content?.hero_sub_en, t('hero', 'sub'));
  const heroCta1 = pick(isAr ? settings?.content?.hero_cta1_ar : settings?.content?.hero_cta1_en, t('hero', 'cta1'));

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#071E2C]">
      {/* Layered Background */}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover gmre-kenburns" />
        {/* Editorial gradient stack */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#071E2C]/95 via-[#0B2F44]/82 to-[#0B2F44]/35" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(201,168,76,0.18),transparent_60%)]" />
        {/* Bronze hairline frame */}
        <div className="absolute inset-x-6 lg:inset-x-12 top-24 h-px bg-gradient-to-r from-transparent via-[#A68966]/40 to-transparent" />
      </div>

      {/* Side ornament: vertical brand mark */}
      <div className="hidden lg:flex absolute end-8 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 text-white/40" aria-hidden>
        <div className="w-px h-16 bg-gradient-to-b from-transparent to-[#C9A84C]" />
        <span className="text-[10px] tracking-[0.4em] [writing-mode:vertical-rl] rotate-180">GULEED · ABU DHABI · 2026</span>
        <div className="w-px h-16 bg-gradient-to-t from-transparent to-[#C9A84C]" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-8 py-32 lg:py-0 w-full">
        <div className="max-w-2xl">
          {/* Overline */}
          <div className="gmre-overline gmre-reveal mb-6" data-testid="hero-overline">
            {isAr ? 'استشارة فاخرة للتمويل العقاري' : 'Private Mortgage Advisory · UAE'}
          </div>

          <h1
            data-testid="hero-h1"
            className="text-4xl sm:text-5xl lg:text-[64px] font-semibold text-white mb-7 gmre-reveal gmre-reveal-delay-1 tracking-tight"
            style={{ fontFamily: displayFont, lineHeight: lang === 'ar' ? '1.45' : '1.08', textWrap: 'balance' }}
          >
            {heroTitle}
          </h1>

          {/* Ornament under headline */}
          <div className="gmre-ornament gmre-reveal gmre-reveal-delay-2 justify-start mb-7">
            <div className="line" style={{ width: 72 }} />
            <div className="dot" />
          </div>

          <p
            className="text-base lg:text-lg text-white/75 mb-10 max-w-xl leading-relaxed gmre-reveal gmre-reveal-delay-3"
            style={{ fontFamily: bodyFont, lineHeight: lang === 'ar' ? '2' : '1.75' }}
          >
            {heroSub}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 gmre-reveal gmre-reveal-delay-4">
            <Link to="/contact">
              <Button
                data-testid="hero-cta-primary"
                className="gmre-btn-sheen bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] hover:text-[#0B2F44] font-semibold text-sm tracking-[0.14em] uppercase px-8 py-6 rounded-none shadow-[0_8px_24px_-8px_rgba(201,168,76,0.6)] hover:shadow-[0_14px_32px_-10px_rgba(201,168,76,0.7)] transition-all duration-500 group"
              >
                {heroCta1}
                <ArrowRight className="w-4 h-4 ms-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                className="gmre-glass border-0 text-white hover:text-[#C9A84C] font-medium text-sm tracking-[0.14em] uppercase px-8 py-6 rounded-none transition-all duration-500"
              >
                {t('hero', 'cta2')}
                <ChevronRight className="w-4 h-4 ms-1" />
              </Button>
            </Link>
            <Link to="/digital-assets">
              <Button
                data-testid="hero-cta-crypto"
                variant="outline"
                className="gmre-glass border-0 text-[#C9A84C] hover:text-white font-medium text-sm tracking-[0.14em] uppercase px-8 py-6 rounded-none transition-all duration-500 group"
              >
                {lang === 'ar' ? 'حوّل عملاتك الرقمية لعقار' : 'Crypto → Property'}
                <Coins className="w-4 h-4 ms-2 group-hover:rotate-12 transition-transform" />
              </Button>
            </Link>
          </div>

          {/* Micro trust pills — glass */}
          <div className="mt-12 flex flex-wrap items-center gap-2 gmre-reveal gmre-reveal-delay-5">
            <span className="gmre-glass-pill inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[10px] text-white/75 tracking-[0.18em] uppercase">
              <Sparkles className="w-3 h-3 text-[#C9A84C]" />{isAr ? 'بدون رسوم' : 'Fee-Free'}
            </span>
            <span className="gmre-glass-pill inline-flex items-center px-3.5 py-1.5 text-[10px] text-white/75 tracking-[0.18em] uppercase">{isAr ? '15+ بنكاً' : '15+ Banks'}</span>
            <span className="gmre-glass-pill inline-flex items-center px-3.5 py-1.5 text-[10px] text-white/75 tracking-[0.18em] uppercase">{isAr ? 'موافقة خلال 48 ساعة' : '48h Pre-Approval'}</span>
            <span className="gmre-glass-pill inline-flex items-center px-3.5 py-1.5 text-[10px] text-white/75 tracking-[0.18em] uppercase">{isAr ? '3+ سنوات خبرة' : '3+ Years'}</span>
          </div>
        </div>

        {/* Floating glass mini-card (desktop only) */}
        <div className="hidden lg:block absolute bottom-24 end-12 max-w-[280px] gmre-reveal gmre-reveal-delay-5">
          <div className="gmre-glass gmre-liquid-sheen rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] text-white/55 tracking-[0.22em] uppercase">{isAr ? 'مباشر · اليوم' : 'Live · Today'}</span>
            </div>
            <p className="text-3xl font-semibold text-white mb-1 tracking-tight" style={{ fontFamily: 'var(--font-en-display)' }} data-testid="hero-live-rate">
              {liveRate?.best_rate || '3.75%'}
            </p>
            <p className="text-[11px] text-white/60 mb-3">
              {liveRate?.product ? (isAr ? `${liveRate.product} · للمقيمين` : `${liveRate.product} · Resident`) : (isAr ? 'أفضل سعر ثابت لـ 1 سنة · للمقيمين' : 'Best 1-Year Fixed · Resident')}
            </p>
            <Link to="/rates" className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#C9A84C] hover:text-white transition-colors">
              {isAr ? 'كل الأسعار' : 'View All Rates'}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50" aria-hidden>
        <span className="text-[10px] tracking-[0.4em] uppercase">{isAr ? 'تمرير' : 'Scroll'}</span>
        <div className="gmre-scroll-line" />
      </div>
    </section>
  );
}

// ─── Trust Strip ───
function TrustStrip() {
  const { t } = useLanguage();
  const metrics = [
    { value: 3, suffix: '+', label: t('trust', 'years'), icon: Shield },
    { value: 15, suffix: '+', label: t('trust', 'partners'), icon: Users },
    { value: 400, suffix: '+', label: t('trust', 'clients'), icon: CheckCircle },
    { value: 50, suffix: 'M+', prefix: 'AED ', label: t('trust', 'arranged'), icon: TrendingUp },
  ];

  return (
    <section data-testid="trust-strip" className="bg-[#071E2C] relative overflow-hidden -mt-12 z-20">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.12),transparent_60%)] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pb-16 relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {metrics.map((m, i) => (
            <div
              key={i}
              data-testid={`trust-metric-${i}`}
              className="gmre-glass gmre-reveal text-center px-5 py-8 lg:py-10 rounded-2xl group"
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div className="w-10 h-10 rounded-full bg-[#C9A84C]/12 border border-[#C9A84C]/25 mx-auto mb-4 flex items-center justify-center group-hover:bg-[#C9A84C]/20 group-hover:border-[#C9A84C]/50 transition-all">
                <m.icon className="w-4 h-4 text-[#C9A84C]" strokeWidth={1.5} />
              </div>
              <div className="text-3xl lg:text-5xl font-semibold text-white mb-2 tracking-tight" style={{ fontFamily: 'var(--font-en-display)' }}>
                <AnimatedCounter end={m.value} suffix={m.suffix} prefix={m.prefix || ''} />
              </div>
              <p className="text-white/55 text-[10px] tracking-[0.24em] uppercase">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Five Steps ───
function FiveSteps() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const isAr = lang === 'ar';
  const defaultSteps = [
    { num: 1, title: t('steps', 's1'), desc: t('steps', 's1d') },
    { num: 2, title: t('steps', 's2'), desc: t('steps', 's2d') },
    { num: 3, title: t('steps', 's3'), desc: t('steps', 's3d') },
    { num: 4, title: t('steps', 's4'), desc: t('steps', 's4d') },
    { num: 5, title: t('steps', 's5'), desc: t('steps', 's5d') },
  ];
  const fromCms = Array.isArray(settings?.content?.steps) ? settings.content.steps.filter(s => s && (s.title_en || s.title_ar)) : [];
  const steps = fromCms.length > 0
    ? fromCms.map((s, i) => ({
        num: s.num || i + 1,
        title: isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar),
        desc: isAr ? (s.desc_ar || s.desc_en) : (s.desc_en || s.desc_ar),
      }))
    : defaultSteps;
  const stepsTitle = pick(isAr ? settings?.content?.steps_title_ar : settings?.content?.steps_title_en, t('steps', 'title'));

  const colsMap = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' };
  const cols = colsMap[Math.min(steps.length, 5)] || 'lg:grid-cols-5';

  return (
    <section data-testid="five-steps" className="bg-[#F8FAFB] py-24 lg:py-32 relative overflow-hidden">
      {/* Editorial backdrop ornament */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(11,47,68,0.04),transparent_60%)] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-16 gmre-reveal">
          <div className="gmre-overline justify-center mb-4">{isAr ? 'كيف نعمل' : 'Our Process'}</div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2F44] tracking-tight"
            style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)' }}
          >
            {stepsTitle}
          </h2>
          <div className="gmre-ornament mt-6"><div className="line" /><div className="dot" /><div className="line" /></div>
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-5`}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-testid={`step-card-${i}`}
              className={`relative bg-white rounded-none p-7 pt-12 border-t border-[#C9D6D9]/60 hover:border-t-[#C9A84C] transition-all duration-500 group gmre-reveal gmre-reveal-delay-${Math.min(i + 1, 5)} overflow-hidden`}
            >
              <span className="gmre-step-num">{String(step.num).padStart(2, '0')}</span>
              <div className="relative">
                <div className="w-10 h-10 border border-[#A68966]/30 text-[#A68966] flex items-center justify-center text-sm font-semibold mb-5 group-hover:bg-[#0B2F44] group-hover:text-[#C9A84C] group-hover:border-[#0B2F44] transition-all duration-500" style={{ fontFamily: 'var(--font-en-display)' }}>
                  {step.num}
                </div>
                <h3 className="font-semibold text-[#0B2F44] mb-2.5 text-lg tracking-tight" style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)' }}>{step.title}</h3>
                <p className="text-sm text-[#4A6572] leading-relaxed">{step.desc}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 -end-2.5 transform text-[#A68966]/40 group-hover:text-[#C9A84C] transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ───
const DEFAULT_TESTIMONIALS = [
  {
    name: 'Ahmed Al-Rashid',
    nationality_en: 'UAE National',
    nationality_ar: 'مواطن إماراتي',
    property_en: 'Villa in Saadiyat Island',
    property_ar: 'فيلا في جزيرة السعديات',
    stars: 5,
    quote_en: 'Guleed Mortgage made the entire mortgage process seamless. Got pre-approved in 24 hours!',
    quote_ar: 'جُليد جعلت عملية التمويل سلسة تماماً. حصلت على الموافقة المبدئية خلال 48 ساعة!',
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY1OTM0NTN8MA&ixlib=rb-4.1.0&q=85&w=200",
  },
  {
    name: 'Sarah Johnson',
    nationality_en: 'British Expat',
    nationality_ar: 'مغتربة بريطانية',
    property_en: 'Apartment in Dubai Marina',
    property_ar: 'شقة في دبي مارينا',
    stars: 5,
    quote_en: 'As an expat, I was overwhelmed by the process. Guleed Mortgage guided me through every step.',
    quote_ar: 'كمغتربة، كنت في حيرة من العملية. جُليد أرشدتني في كل خطوة.',
    img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY1OTM0NTN8MA&ixlib=rb-4.1.0&q=85&w=200",
  },
  {
    name: 'Raj Patel',
    nationality_en: 'Indian Investor',
    nationality_ar: 'مستثمر هندي',
    property_en: 'Penthouse in Abu Dhabi',
    property_ar: 'بنتهاوس في أبوظبي',
    stars: 5,
    quote_en: "Converted my crypto to property with Guleed Mortgage's help. Truly unique service in the UAE.",
    quote_ar: 'حولت عملاتي الرقمية لعقار بمساعدة جُليد. خدمة فريدة في الإمارات.',
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY1OTM0NTN8MA&ixlib=rb-4.1.0&q=85&w=200",
  },
];

function Testimonials() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const isAr = lang === 'ar';
  const fromCms = Array.isArray(settings?.content?.testimonials) ? settings.content.testimonials.filter(Boolean) : [];
  const testimonials = fromCms.length > 0 ? fromCms : DEFAULT_TESTIMONIALS;

  return (
    <section data-testid="testimonials-section" className="bg-white py-24 lg:py-32 relative">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 gmre-reveal">
          <div className="gmre-overline justify-center mb-4">{isAr ? 'آراء العملاء' : 'Client Voices'}</div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2F44] tracking-tight"
            style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)' }}
          >
            {t('testimonials', 'title')}
          </h2>
          <div className="gmre-ornament mt-6"><div className="line" /><div className="dot" /><div className="line" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((item, i) => (
            <div
              key={i}
              data-testid={`testimonial-card-${i}`}
              className={`gmre-quote-glyph relative bg-[#F8FAFB] p-10 pt-16 border-t border-[#C9D6D9] hover:bg-white hover:border-t-[#C9A84C] hover:shadow-[0_30px_60px_-30px_rgba(11,47,68,0.18)] hover:-translate-y-1 transition-all duration-700 group gmre-reveal gmre-reveal-delay-${Math.min(i + 1, 3)}`}
            >
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: item.stars || 5 }).map((_, si) => (
                  <Star key={si} className="w-3.5 h-3.5 text-[#C9A84C] fill-[#C9A84C]" />
                ))}
              </div>
              <p
                className="text-[#1A2B35] text-base leading-relaxed mb-7 font-light"
                style={{
                  fontFamily: lang === 'ar' ? 'var(--font-ar-body)' : 'var(--font-en-display)',
                  lineHeight: lang === 'ar' ? '2' : '1.7',
                  fontStyle: lang === 'ar' ? 'normal' : 'italic',
                }}
              >
                {isAr ? (item.quote_ar || item.quote_en) : (item.quote_en || item.quote_ar)}
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-[#C9D6D9]/60">
                {item.img && <img src={item.img} alt={item.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-[#C9A84C]/30" />}
                <div className="flex-1">
                  <p className="font-semibold text-[#0B2F44] text-sm tracking-tight">{item.name}</p>
                  <p className="text-[11px] text-[#A68966] tracking-[0.12em] uppercase mt-0.5">{isAr ? (item.nationality_ar || item.nationality_en) : (item.nationality_en || item.nationality_ar)}</p>
                </div>
              </div>
              <p className="text-[10px] text-[#4A6572] mt-3 tracking-wider">{isAr ? (item.property_ar || item.property_en) : (item.property_en || item.property_ar)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Lead Capture ───
function LeadCapture() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();
  const isAr = lang === 'ar';
  const [form, setForm] = useState({ full_name: '', phone: '', enquiry_type: '' });
  const [status, setStatus] = useState('');

  const leadTitle = pick(isAr ? settings?.content?.lead_title_ar : settings?.content?.lead_title_en, t('lead', 'title'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.enquiry_type) return;
    try {
      await axios.post(`${API}/leads`, form);
      setStatus('success');
      setForm({ full_name: '', phone: '', enquiry_type: '' });
    } catch {
      setStatus('error');
    }
  };

  const enquiryOptions = lang === 'ar'
    ? ['شراء أول عقار', 'عقار استثماري', 'تمويل غير المقيم', 'إعادة التمويل', 'العملات الرقمية']
    : ['First Home Purchase', 'Property Investment', 'Non-Resident Mortgage', 'Refinance', 'Crypto to Property'];

  return (
    <section data-testid="lead-capture" className="bg-[#0B2F44] py-20 lg:py-24">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: lang === 'ar' ? 'var(--font-ar-display)' : 'var(--font-en-display)' }}
          >
            {leadTitle}
          </h2>
          <form onSubmit={handleSubmit} className="mt-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Input
                data-testid="lead-name-input"
                placeholder={t('lead', 'fullName')}
                value={form.full_name}
                onChange={e => setForm({ ...form, full_name: e.target.value })}
                className="h-[52px] bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#A68966]"
              />
              <Input
                data-testid="lead-phone-input"
                placeholder={t('lead', 'phone')}
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="h-[52px] bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#A68966]"
              />
              <Select value={form.enquiry_type} onValueChange={v => setForm({ ...form, enquiry_type: v })}>
                <SelectTrigger data-testid="lead-enquiry-select" className="h-[52px] bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder={t('lead', 'enquiryType')} />
                </SelectTrigger>
                <SelectContent>
                  {enquiryOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              data-testid="lead-submit-btn"
              className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold px-10 py-6 text-base"
            >
              {t('lead', 'submit')}
            </Button>
            {status === 'success' && <p className="text-[#1A7A4A] mt-4 text-sm">{lang === 'ar' ? 'تم الإرسال بنجاح!' : 'Submitted successfully!'}</p>}
            {status === 'error' && <p className="text-[#C0392B] mt-4 text-sm">{lang === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.'}</p>}
          </form>
        </div>
      </div>
    </section>
  );
}

// ─── Main HomePage ───
export default function HomePage() {
  useReveal();
  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustStrip />
      <FiveSteps />
      <LeadCapture />
    </div>
  );
}
